import EditIcon from "@mui/icons-material/Edit";
import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { useAuth } from "@/providers";
import { PageBreadcrumbs } from "@/shared/components";
import { ExpandableCopyText } from "@/shared/components/ExpandableCopyText";
// Deep import, not the barrel: browser tests stub this module to skip the
// app-shell chrome (sidebar toggle, top-bar notifications) it pulls in.
import { PageLayout } from "@/shared/components/PageLayout";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { canEditSkill, SkillVersionRecord } from "@/shared/domain/skills";
import {
  useSkillSummary,
  useSkillVersions,
  useSubscribeSkill,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import { useOrgTopicNames } from "@/shared/domain/topics/hooks";
import { Button } from "@/shared/foundation/primitives";
import { RobotoNotFoundException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { SkillAccessibilityBanner } from "./SkillAccessibilityBanner";
import styles from "./SkillDetail.module.css";
import {
  PROCEDURE_HINT,
  PROCEDURE_LABEL,
  WHEN_TO_USE_HINT,
  WHEN_TO_USE_LABEL,
} from "./skillFieldCopy";
import { SkillFieldSection } from "./SkillFieldSection";
import { SkillHeader } from "./SkillHeader";
import {
  parseSkillVersionParam,
  skillContentSeedFromLocationState,
  SKILL_EDIT_PARAM,
} from "./skillLinks";
import { SkillVersionFields } from "./SkillVersionFields";
import { useSkillVersionForm } from "./skillVersionForm";
import { SkillVersionSelect } from "./SkillVersionSelect";

type Mode = "view" | "edit";

function latestOf(versions: SkillVersionRecord[]): SkillVersionRecord | null {
  if (versions.length === 0) {
    return null;
  }
  return versions.reduce((a, b) => (a.version > b.version ? a : b));
}

/**
 * A skill's own page: read any version's body, switch between versions, and —
 * with edit rights — rewrite one in place or publish it as a new version.
 *
 * This is what a share link points at. The id lives in the path
 * (`/agents/skills/:skillId`, optionally `/versions/v:version`), so opening one
 * fetches exactly that skill: no list to page through, no Personal / Org tab to
 * guess at, no filter to seed. Which tab the *list* files a skill under depends
 * on the reader's own relationship to it, and that question simply doesn't
 * arise here.
 *
 * The version shown and the URL stay in lockstep — picking a version navigates
 * — so the copy-link button always hands over what's on screen.
 */
export function SkillDetail() {
  const { skillId, version: versionParam } = useParams<{
    skillId: string;
    version?: string;
  }>();
  const navigate = useNavigate();
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id;
  const userId = currentUser?.user_id ?? "";

  const summaryQuery = useSkillSummary(orgId, skillId);
  const versionsQuery = useSkillVersions(skillId);
  const subscribeSkill = useSubscribeSkill(orgId);
  const summary = summaryQuery.data ?? null;

  // Edit mode lives in the URL, not in component state: that's what lets the
  // Skills table's per-version pen icon be a plain link into this page rather
  // than a second editing surface. It also means a reload mid-edit comes back
  // to the form, and navigating to another version arrives in whatever mode
  // that URL asks for.
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode =
    searchParams.get(SKILL_EDIT_PARAM) === null ? "view" : "edit";
  const setMode = React.useCallback(
    (next: Mode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "edit") {
            params.set(SKILL_EDIT_PARAM, "1");
          } else {
            params.delete(SKILL_EDIT_PARAM);
          }
          return params;
        },
        // `replace`: toggling a mode isn't a place to go Back to. Without it,
        // leaving the page would mean clicking Back once per toggle.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const requestedVersion = parseSkillVersionParam(versionParam);
  const versions = React.useMemo(
    () => versionsQuery.data ?? [],
    [versionsQuery.data],
  );
  const latest = latestOf(versions);
  const pinned =
    requestedVersion === null
      ? null
      : versions.find((v) => v.version === requestedVersion) ?? null;
  const chosen = pinned ?? latest;

  // The URL named a version we can't show: either it isn't a version at all
  // (hand-edited path) or it has since been deleted. Say so rather than
  // rendering some other version's body under the version number in the
  // address bar — the reader would have no way to tell they're reading a
  // substitute, and with edit rights they could overwrite it.
  const unresolvedVersion =
    versionParam !== undefined && pinned === null && versionsQuery.isSuccess
      ? versionParam
      : null;

  const canEdit = summary !== null && canEditSkill(summary, userId);
  // An unresolvable version refuses edit mode outright rather than quietly
  // retargeting. `chosen` falls back to the latest version so there is still
  // something to read, but binding the *form* to that fallback would mean a
  // Save overwrote a body the URL never named — and "Save as new version"
  // published a branch of the wrong parent. Neither is recoverable from the
  // screen, so the page declines to edit anything and says why.
  const isEditing = mode === "edit" && canEdit && unresolvedVersion === null;
  // AI-proposed content from the chat tool's update handoff, riding along as
  // router state on the navigation that opened this page in edit mode. Memoed
  // so the form sees a stable value per navigation; any in-page navigation
  // (mode toggle, version switch, save) drops the state and with it the seed —
  // which is right, since by then the user has either kept or abandoned it.
  const location = useLocation();
  const editSeed = React.useMemo(
    () => skillContentSeedFromLocationState(location.state),
    [location.state],
  );
  const form = useSkillVersionForm({
    active: isEditing,
    summary,
    version: chosen?.version ?? null,
    orgId,
    seed: editSeed,
  });

  // Tag changes the seed proposed, held back until the user saves. Everywhere
  // else on this page a tag writes the moment it's typed, but these were
  // written by the AI, and the editor is where the user accepts or rejects the
  // proposal — so they stage here and go out with the same Save that accepts
  // the body. Reset per navigation (`editSeed` is memoed on `location.state`),
  // which is also what drops them when the user arrives some other way.
  const [proposedTags, setProposedTags] = React.useState<string[]>([]);
  const [proposedRemovals, setProposedRemovals] = React.useState<string[]>([]);
  React.useEffect(() => {
    setProposedTags(editSeed?.tags ?? []);
    setProposedRemovals(editSeed?.removeTags ?? []);
  }, [editSeed]);
  // Both lists are narrowed to changes that would actually change something.
  // The model proposes the tag set it thinks the skill should have, not a
  // diff, so re-proposing an existing tag — or dropping one the skill never
  // had — is normal, and must not show as a pending change or produce a write
  // that does nothing.
  const existingTags = summary?.skill.tags;
  const stagedTags = React.useMemo(
    () => proposedTags.filter((tag) => !existingTags?.includes(tag)),
    [proposedTags, existingTags],
  );
  const stagedRemovals = React.useMemo(
    () => proposedRemovals.filter((tag) => existingTags?.includes(tag)),
    [proposedRemovals, existingTags],
  );
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const [tagError, setTagError] = React.useState<string | null>(null);

  /**
   * Apply the staged tag changes, reporting whether the page may leave edit
   * mode.
   *
   * One request for both directions, so a proposal can't half-apply. It runs
   * after the version write rather than before it: a failed save with the tags
   * already changed would leave the skill holding part of a proposal the user
   * never got. On failure the changes stay staged and the page stays in edit
   * mode so Save can be pressed again — the version write that already
   * succeeded simply rewrites the same content.
   */
  async function commitStagedTags(): Promise<boolean> {
    if (
      (stagedTags.length === 0 && stagedRemovals.length === 0) ||
      skillId === undefined
    ) {
      return true;
    }
    setTagError(null);
    try {
      await updateMetadata.mutateAsync({
        skillId,
        request: {
          ...(stagedTags.length > 0 ? { put_tags: stagedTags } : {}),
          ...(stagedRemovals.length > 0 ? { remove_tags: stagedRemovals } : {}),
        },
      });
      setProposedTags([]);
      setProposedRemovals([]);
      return true;
    } catch (err) {
      setTagError(
        err instanceof Error
          ? err.message
          : "Saved the version, but couldn't apply the proposed tag changes.",
      );
      return false;
    }
  }

  /** Leave edit mode, abandoning the AI's proposal along with any typing. */
  function cancelEdit() {
    setProposedTags([]);
    setProposedRemovals([]);
    setTagError(null);
    setMode("view");
  }

  // Org topic catalog for styling `[[topic]]` references in the body as known
  // vs. not-yet-known — the same chips the editor shows while authoring. Until
  // it resolves (or if it errors) the body renders without reference chips.
  const topicNamesQuery = useOrgTopicNames(summary?.skill.org_id);
  const knownTopicNames = topicNamesQuery.data;

  async function handleSave() {
    if (!(await form.save())) {
      return;
    }
    if (await commitStagedTags()) {
      setMode("view");
    }
  }

  async function handleSaveAsNewVersion() {
    const created = await form.saveAsNewVersion();
    if (created === null || skillId === undefined) {
      return;
    }
    // Tags belong to the skill, not to the version, so which save path the
    // user took can't decide whether the proposed ones are applied.
    if (!(await commitStagedTags())) {
      return;
    }
    // Land on what was just published rather than on the version it branched
    // from. Navigating to the bare version URL also drops `?edit=1`, so the
    // page settles on the new body in read mode.
    void navigate(Goto.createSkillRoute(skillId, created.version));
  }

  const headerContentLeft = (
    <PageBreadcrumbs>
      <Link to={Goto.route.AgentSkills}>Skills</Link>
      {/* The id, not the name: the name is the page's own editable title, and
          repeating it here would give one field two homes. Matches the dataset
          page. */}
      <ExpandableCopyText text={skillId ?? ""} trimLength={15} />
    </PageBreadcrumbs>
  );
  const pageTitle = `Roboto - ${summary?.skill.name ?? "Skill"}`;

  if (skillId === undefined) {
    // Unreachable through the router (the segment is required to match this
    // route); guards the non-null assertions below for anyone rendering it
    // directly.
    return null;
  }

  if (orgId === undefined || summaryQuery.isLoading) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.page}>
          <CircularProgress size={20} />
        </div>
      </PageLayout>
    );
  }

  if (summaryQuery.isError || summary === null) {
    // Only a genuine 404 means "you can't see this skill". Everything else —
    // a 5xx, a network drop, an endpoint the deployed backend doesn't have yet
    // — is a failure to load, and saying "isn't shared with you" for those
    // sends the reader off chasing a permissions problem that doesn't exist.
    // The server *does* answer "no such skill" and "not shared with you"
    // identically (see `ensure_skill_visible`), so that one message covers both.
    const notVisible = summaryQuery.error instanceof RobotoNotFoundException;
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.page}>
          {/* The way out lives *inside* the alert. There is nothing else on
              this page — a link floating below the banner reads as unrelated
              page furniture rather than as the answer to "so what now?". */}
          {notVisible ? (
            <Alert severity="info">
              This skill ({skillId}) isn&apos;t in this organization, or
              isn&apos;t shared with you. Ask whoever sent the link to make it
              org-visible, or{" "}
              <Link to={Goto.route.AgentSkills}>go back to all skills</Link>.
            </Alert>
          ) : (
            <Alert severity="error">
              Couldn&apos;t load this skill ({skillId}).{" "}
              {summaryQuery.error?.message ?? "Try reloading the page."}{" "}
              <Link to={Goto.route.AgentSkills}>Back to all skills</Link>.
            </Alert>
          )}
        </div>
      </PageLayout>
    );
  }

  let bodyContent: React.ReactNode;
  if (versionsQuery.isLoading) {
    bodyContent = <CircularProgress size={20} />;
  } else if (versionsQuery.isError) {
    bodyContent = <Alert severity="error">Couldn&apos;t load versions.</Alert>;
  } else if (isEditing) {
    bodyContent = <SkillVersionFields form={form} orgId={orgId} />;
  } else if (chosen) {
    bodyContent = (
      <>
        <SkillFieldSection title={WHEN_TO_USE_LABEL} hint={WHEN_TO_USE_HINT}>
          <p className={styles.description}>{chosen.description}</p>
        </SkillFieldSection>
        <SkillFieldSection title={PROCEDURE_LABEL} hint={PROCEDURE_HINT} grow>
          <div className={styles.body}>
            <RobotoMarkdown
              content={chosen.body}
              variant="compact"
              topicReferences={
                knownTopicNames !== undefined ? { knownTopicNames } : undefined
              }
            />
          </div>
        </SkillFieldSection>
      </>
    );
  } else {
    // Defense-in-depth: every skill has ≥1 version as an invariant (deleting
    // the last one cascades into deleting the skill). Reaching this branch
    // implies a future bug, not a live state.
    bodyContent = <p>This skill has no versions.</p>;
  }

  return (
    <PageLayout
      title={pageTitle}
      headerContentLeft={headerContentLeft}
      footer={
        isEditing ? (
          <div className={styles.footerActions}>
            <Button onClick={cancelEdit} disabled={form.isMutating}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveAsNewVersion()}
              disabled={form.isSaveBlocked}
            >
              Save as new version
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={form.isSaveBlocked}
              loading={form.isMutating}
            >
              Save
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className={styles.page}>
        {unresolvedVersion !== null && (
          <Alert severity="warning">
            This link points at version &ldquo;{unresolvedVersion}&rdquo;, which
            this skill doesn&apos;t have — it may have been deleted since the
            link was shared.
            {chosen !== null && ` Showing v${chosen.version} instead.`}
            {mode === "edit" &&
              canEdit &&
              " Editing is off until you pick a version that exists."}
          </Alert>
        )}

        <SkillAccessibilityBanner
          summary={summary}
          userId={userId}
          onSubscribe={() => subscribeSkill.mutate({ skillId })}
          isSubscribing={subscribeSkill.isPending}
        />

        {tagError !== null && (
          <Alert severity="error" onClose={() => setTagError(null)}>
            {tagError}
          </Alert>
        )}

        <SkillHeader
          summary={summary}
          canEdit={canEdit}
          isAuthor={summary.skill.created_by === userId}
          orgId={orgId}
          version={chosen}
          // Only offered while editing: staging exists so the editor's Save is
          // what applies them, and there is no Save outside edit mode.
          stagedTags={isEditing ? stagedTags : []}
          stagedRemovals={isEditing ? stagedRemovals : []}
          onDropStagedTag={(tag) =>
            setProposedTags((prev) => prev.filter((t) => t !== tag))
          }
          onKeepTag={(tag) =>
            setProposedRemovals((prev) => prev.filter((t) => t !== tag))
          }
          actions={
            <>
              <SkillVersionSelect
                versions={versions}
                selected={chosen?.version ?? null}
                latest={latest?.version ?? null}
                // Switching versions mid-edit would silently retarget the
                // save; Cancel is the way out.
                disabled={isEditing}
                onChange={(version) => {
                  void navigate(Goto.createSkillRoute(skillId, version));
                }}
              />
              {/* One labelled entrance to edit mode. The per-version pen this
                  replaced lived inside the version rail, where readers didn't
                  find it. It edits whichever version is on screen. */}
              {canEdit && !isEditing && unresolvedVersion === null && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => setMode("edit")}
                >
                  Edit skill
                </Button>
              )}
            </>
          }
        />

        <div className={styles.content}>{bodyContent}</div>
      </div>
    </PageLayout>
  );
}
